
<template>
  <div
    id="end-day-check-modal"
    ref="modalEl"
    class="modal core-materialize-modal core-materialize-modal--medium"
  >
    <div class="modal-content">
      <h5 class="modal-title">Fire Safety — Closing Checks</h5>

      <p class="grey-text text-darken-2">
        Record the closing checks completed. Any unanswered checks
        will be marked Not Completed and will not prevent End Day.
      </p>

      <div
        v-for="q in questions"
        :key="q.id"
        class="card-panel closing-question"
      >
        <p class="question-title">{{ q.label }}</p>

        <p v-if="q.help" class="question-help">
          {{ q.help }}
        </p>

        <div class="answer-row">
          <label
            v-for="choice in ['yes', 'no']"
            :key="choice"
          >
            <input
              v-model="answers[q.id].answer"
              type="radio"
              :name="'close_' + q.id"
              :value="choice"
              :disabled="submitting"
            />
            <span>{{ choice === "yes" ? "Yes" : "No" }}</span>
          </label>

          <button
            v-if="answers[q.id].answer"
            type="button"
            class="btn-flat clear-answer"
            :disabled="submitting"
            @click="clearAnswer(q.id)"
          >
            Clear
          </button>
        </div>

        <div
          v-if="answers[q.id].answer === 'no'"
          class="comment-box"
        >
          <label class="active">
            Defect / action taken (optional)
          </label>

          <textarea
            v-model.trim="answers[q.id].comment"
            class="browser-default"
            :disabled="submitting"
            placeholder="Explain the problem and action taken, if known"
          ></textarea>
        </div>

        <p
          v-if="!answers[q.id].answer"
          class="not-completed-note"
        >
          Not answered — will be recorded as Not Completed.
        </p>
      </div>

      <p class="warning-note">
        Do not secure final exits until the building is
        clear of occupants and they are no longer required
        as means of escape.
      </p>

      <p v-if="error" class="red-text">
        {{ error }}
      </p>
    </div>

    <div class="modal-footer closing-footer">
      <button
        type="button"
        class="btn-flat"
        :disabled="submitting"
        @click="close"
      >
        Cancel
      </button>

      <button
        type="button"
        class="btn teal darken-1"
        :disabled="submitting"
        @click="submitForm"
      >
        {{ submitting ? "Processing..." : "Complete & End Day" }}
      </button>
    </div>
  </div>
</template>

<script setup>
import {
  reactive,
  ref,
  onMounted,
  onBeforeUnmount,
} from "vue";

defineProps({
  submitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["submit"]);

const modalEl = ref(null);
const error = ref("");

let modalInstance = null;

const questions = [
  {
    id: "incidents",
    label: "Have all incidents been logged as an incident form?",
  },
  {
    id: "fire_hazards",
    label: "Have all areas been checked for fire hazards and combustible materials?",
  },
  {
    id: "toilets",
    label: "Have all public toilets and cubicles been inspected?",
    help: "Check for rubbish, debris and potential fire hazards.",
  },
  {
    id: "waste",
    label: "Has combustible waste been removed and stored safely?",
    help: "Include exterior bins and areas vulnerable to arson.",
  },
  {
    id: "electrical",
    label: "Has unnecessary electrical equipment been switched off or unplugged?",
  },
  {
    id: "windows_doors",
    label: "Have windows and doors been closed as appropriate?",
  },
  {
    id: "exits_secure",
    label: "Are final exit doors, gates and shutters correctly secured?",
    help: "Only once occupants have left and escape routes are no longer required.",
  },
];

const answers = reactive(
  Object.fromEntries(
    questions.map((q) => [
      q.id,
      {
        answer: "",
        comment: "",
      },
    ])
  )
);

function clearAnswer(id) {
  answers[id].answer = "";
  answers[id].comment = "";
}

function open() {
  error.value = "";

  resetForm();

  modalInstance?.open();
}

function close() {
  modalInstance?.close();
}

function resetForm() {
  questions.forEach((q) => {
    answers[q.id].answer = "";
    answers[q.id].comment = "";
  });

  error.value = "";
}

function submitForm() {
  const checks = {};

  for (const q of questions) {
    const response = answers[q.id];

    const answer =
      response.answer === "yes" ||
      response.answer === "no"
        ? response.answer
        : "not_completed";

    checks[q.id] = {
      label: q.label,
      answer,
      comment:
        answer === "no"
          ? String(response.comment || "").trim()
          : "",
    };
  }

  // No validation barrier.
  // Unanswered items are recorded, not treated as passed.
  // No answers may be recorded without a comment.

  error.value = "";

  emit("submit", { checks });
}

defineExpose({
  open,
  close,
  resetForm,
});

onMounted(() => {
  if (modalEl.value && window.M?.Modal) {
    modalInstance = window.M.Modal.init(
      modalEl.value,
      {
        dismissible: false,
      }
    );
  }
});

onBeforeUnmount(() => {
  modalInstance?.destroy?.();
});
</script>

<style scoped>
.modal-title {
  margin-top: 0;
  color: #263238;
}

.closing-question {
  margin: 12px 0;
  padding: 15px;
  border-radius: 10px;
}

.question-title {
  margin: 0 0 10px;
  font-weight: 600;
  color: #263238;
}

.question-help {
  margin: -4px 0 10px;
  color: #607d8b;
  font-size: 13px;
}

.answer-row {
  display: flex;
  align-items: center;
  gap: 25px;
  flex-wrap: wrap;
}

.clear-answer {
  margin-left: auto;
  height: 30px;
  line-height: 30px;
  padding: 0 8px;
  font-size: 12px;
  color: #607d8b;
  text-transform: none;
}

.comment-box {
  margin-top: 14px;
}

.comment-box label {
  display: block;
  margin-bottom: 7px;
  font-size: 13px;
  color: #455a64;
}

.comment-box textarea {
  box-sizing: border-box;
  width: 100%;
  min-height: 80px;
  padding: 10px;
  border: 1px solid #cfd8dc;
  border-radius: 7px;
  resize: vertical;
}

.not-completed-note {
  margin: 9px 0 0;
  font-size: 12px;
  color: #b71c1c;
}

.warning-note {
  padding: 12px;
  background: #fff8e1;
  border-left: 4px solid #ff9800;
  font-size: 13px;
}

.closing-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 18px !important;
}
</style>
